import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import BrandLogo from '@/components/brand/BrandLogo';
import '@/styles/special-day.css';

const BARS = 92;
const PLAY_MS = 23000;
const LINES = [
  'Wishing everyone a happy 80th Independence Day, celebrating in India or abroad.',
  'To all those healthcare professionals, we salute your work.',
];
const TREATMENTS = [
  'Bone marrow transplant',
  'CAR-T therapy',
  'High-dose chemotherapy',
  'Total body irradiation',
  'HIPEC',
  'Whipple surgery',
  'Stem cell transplant',
  'TIL therapy',
  'Chemoradiation',
  'Proton therapy',
  'Cytoreductive surgery',
  'Isolated limb perfusion',
  'Pelvic exenteration',
  'Allogeneic transplant',
  'Craniospinal radiation',
  'Intra-arterial chemo',
  'Radioembolization',
  'High-dose methotrexate',
  'Intrathecal chemotherapy',
  'Intraoperative radiation',
  'Stereotactic radiosurgery',
  'Carbon ion therapy',
  'Myeloablative conditioning',
];

function publicAsset(path) {
  const base = String(process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${path}`;
}

const AUDIO_SRC = publicAsset('/specialday/wish.wav');

function FlagMark() {
  return (
    <svg className="special-day-flag" viewBox="0 0 9 6" aria-hidden="true">
      <rect width="9" height="2" fill="#FF9933" />
      <rect y="2" width="9" height="2" fill="#fff" />
      <rect y="4" width="9" height="2" fill="#138808" />
      <circle cx="4.5" cy="3" r=".7" fill="none" stroke="#000080" strokeWidth=".22" />
    </svg>
  );
}

function ReplayGlyph() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4.8 12a7.2 7.2 0 1 0 2.1-5.1" strokeLinecap="round" />
      <path d="M4.6 4.8v4.2h4.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function SpecialDayPage() {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const freqRef = useRef(null);
  const playingRef = useRef(false);
  const deadRef = useRef(false);
  const [phase, setPhase] = useState('idle');
  const [caption, setCaption] = useState('');
  const [caret, setCaret] = useState(false);
  const [treatment, setTreatment] = useState(TREATMENTS[0]);

  useEffect(() => {
    document.documentElement.classList.add('special-day-lock');
    return () => document.documentElement.classList.remove('special-day-lock');
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext && canvas.getContext('2d');
    if (!ctx) return undefined;

    const level = new Float32Array(BARS);
    const seed = Array.from({ length: BARS }, () => Math.random() * Math.PI * 2);
    let phaseT = Math.random() * 99;
    let energy = 0.16;
    let raf = 0;
    let size = { w: 0, h: 0 };
    let cancelled = false;

    const fit = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      canvas.width = r.width * dpr;
      canvas.height = r.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      size = { w: r.width, h: r.height };
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(canvas);

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const draw = () => {
      if (cancelled) return;
      const { w, h } = size;
      if (w) {
        ctx.clearRect(0, 0, w, h);
        const cx = w / 2;
        const cy = h / 2;
        const S = Math.min(w, h);
        const r0 = S * 0.33;
        const maxLen = S * 0.128;
        phaseT += playingRef.current ? 0.055 : 0.028;

        let live = 0;
        const analyser = analyserRef.current;
        const freq = freqRef.current;
        if (playingRef.current && analyser && freq) {
          analyser.getByteFrequencyData(freq);
          let sum = 0;
          for (let i = 0; i < freq.length; i += 1) sum += freq[i];
          live = Math.min(1, (sum / (freq.length * 255)) * 3.2);
        }
        const target = playingRef.current ? 0.28 + live * 0.72 : 0.16;
        energy += (target - energy) * 0.08;

        for (let i = 0; i < BARS; i += 1) {
          const s = Math.sin(phaseT * 1.5 + seed[i]) * Math.sin(phaseT * 0.61 + seed[i] * 1.7);
          let t = 0.08 + Math.abs(s) * 0.8 * energy + ((Math.sin(phaseT * 0.5 - i * 0.14) + 1) / 2) * 0.09;
          if (playingRef.current && freq) {
            const bin = freq[Math.floor((i / BARS) * freq.length)] || 0;
            t = Math.max(t, 0.1 + (bin / 255) * energy);
          }
          level[i] += (t - level[i]) * (t > level[i] ? 0.42 : 0.14);
          const v = Math.min(level[i], 1.25);
          const a = (i / BARS) * Math.PI * 2 - Math.PI / 2;
          const len = maxLen * v;
          ctx.strokeStyle = `hsl(${250 - (i / BARS) * 88} 92% ${62 - Math.min(v, 1) * 20}%)`;
          ctx.lineWidth = Math.max(2, S * 0.0092);
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
          ctx.lineTo(cx + Math.cos(a) * (r0 + len), cy + Math.sin(a) * (r0 + len));
          ctx.stroke();
        }
      }
      if (!reduced) raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, []);

  useEffect(() => {
    if (phase !== 'playing') {
      setCaret(false);
      return undefined;
    }
    let cancelled = false;
    let li = 0;
    let ci = 0;
    let del = false;
    let timer = 0;
    setCaret(true);

    const type = () => {
      if (cancelled) return;
      const line = LINES[li];
      ci += del ? -1 : 1;
      setCaption(line.slice(0, Math.max(0, ci)));
      if (!del && ci === line.length) {
        del = true;
        timer = window.setTimeout(type, 2200);
        return;
      }
      if (del && ci === 0) {
        del = false;
        li = (li + 1) % LINES.length;
        timer = window.setTimeout(type, 280);
        return;
      }
      timer = window.setTimeout(type, del ? 16 : 42 + Math.random() * 36);
    };
    timer = window.setTimeout(type, 240);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing') {
      setTreatment(TREATMENTS[0]);
      return undefined;
    }
    const step = PLAY_MS / TREATMENTS.length;
    let i = 0;
    setTreatment(TREATMENTS[0]);
    const tick = window.setInterval(() => {
      i = (i + 1) % TREATMENTS.length;
      setTreatment(TREATMENTS[i]);
    }, step);
    return () => window.clearInterval(tick);
  }, [phase]);

  const finish = useCallback(() => {
    if (deadRef.current) return;
    playingRef.current = false;
    setPhase('done');
    setCaret(false);
  }, []);

  const armAudio = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return null;
    audio.playsInline = true;
    audio.setAttribute('playsinline', '');
    audio.setAttribute('webkit-playsinline', '');
    audio.crossOrigin = 'anonymous';
    let ctx = ctxRef.current;
    if (!ctx) {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        ctx = new AC();
        ctxRef.current = ctx;
        const source = ctx.createMediaElementSource(audio);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.72;
        source.connect(analyser);
        analyser.connect(ctx.destination);
        analyserRef.current = analyser;
        freqRef.current = new Uint8Array(analyser.frequencyBinCount);
      }
    }
    if (ctx && ctx.state === 'suspended') await ctx.resume();
    return audio;
  }, []);

  const start = useCallback(async () => {
    if (playingRef.current || deadRef.current) return;
    const audio = audioRef.current;
    if (!audio) return;
    setCaption('');
    try {
      await armAudio();
      audio.currentTime = 0;
      await audio.play();
      if (deadRef.current) return;
      playingRef.current = true;
      setPhase('playing');
    } catch (_) {
      finish();
    }
  }, [armAudio, finish]);

  const onRing = useCallback(() => {
    if (phase === 'playing') return;
    start();
  }, [phase, start]);

  useEffect(() => {
    return () => {
      deadRef.current = true;
      playingRef.current = false;
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.removeAttribute('src');
      }
      const ctx = ctxRef.current;
      if (ctx) ctx.close().catch(() => {});
    };
  }, []);

  const hint = phase === 'playing' ? '' : phase === 'done' ? 'tap to replay' : 'tap the ring';

  return (
    <div className="special-day" data-testid="special-day">
      <Helmet>
        <title>ByOnco</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
      </Helmet>
      <div className="special-day-bar" aria-hidden="true">
        <span /><span /><span />
      </div>
      <div className="special-day-inner">
        <div className="special-day-brand">
          <BrandLogo className="special-day-logo" size="md" />
          <p className="special-day-cred">trained by oncologists and healthcare professionals</p>
        </div>

        <div className="special-day-stage">
          <button
            type="button"
            className={`special-day-ring${phase === 'playing' ? ' is-playing' : ''}`}
            onClick={onRing}
            aria-label={phase === 'done' ? 'Replay' : 'Play wishes'}
          >
            <canvas ref={canvasRef} />
            <div className="special-day-core">
              {phase === 'done' ? (
                <ReplayGlyph />
              ) : (
                <span key={treatment} className="special-day-tx">{treatment}</span>
              )}
            </div>
          </button>
          <p className="special-day-words" aria-live="polite">
            <span className="typed">{caption}</span>
            <span className={`special-day-caret${caret ? ' is-on' : ''}`} aria-hidden="true" />
          </p>
          <p className="special-day-hint">{hint}</p>
        </div>

        <div className="special-day-foot">
          <p className="special-day-wish">
            <FlagMark />
            <span>15 August 2026</span>
          </p>
        </div>
      </div>

      <audio
        ref={audioRef}
        src={AUDIO_SRC}
        preload="auto"
        playsInline
        crossOrigin="anonymous"
        onEnded={finish}
        onError={() => {
          if (!deadRef.current && playingRef.current) finish();
        }}
      />
    </div>
  );
}
